/**
 * Audio Source Selection Dialog
 * ==============================
 * Allows user to choose between Browser microphone (AmbeX) or Hardware cabin microphone
 * before starting a consultation session.
 *
 * Part of: Jetson Hybrid Audio Integration
 * Created: 2026-01-05
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Typography,
  Box,
  Paper,
  Alert
} from '@mui/material';
import { Mic, Computer, Cabin } from '@mui/icons-material';

export type AudioSourceType = 'browser' | 'hardware';

interface AudioSourceDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (audioSource: AudioSourceType) => void;
}

export default function AudioSourceDialog({
  open,
  onClose,
  onSelect
}: AudioSourceDialogProps) {
  const [selectedSource, setSelectedSource] = React.useState<AudioSourceType>('browser');

  const handleConfirm = () => {
    onSelect(selectedSource);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Mic color="primary" />
          <Typography variant="h6">Select Audio Source</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Choose how you want to capture audio for this consultation
        </Alert>

        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ mb: 1 }}>
            Audio Input Mode
          </FormLabel>

          <RadioGroup
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value as AudioSourceType)}
          >
            {/* Browser Microphone Option */}
            <Paper
              elevation={selectedSource === 'browser' ? 3 : 1}
              sx={{
                p: 2,
                mb: 2,
                border: selectedSource === 'browser' ? 2 : 1,
                borderColor: selectedSource === 'browser' ? 'primary.main' : 'divider',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  elevation: 3,
                  borderColor: 'primary.main'
                }
              }}
              onClick={() => setSelectedSource('browser')}
            >
              <FormControlLabel
                value="browser"
                control={<Radio />}
                label={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Computer fontSize="small" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        Browser Microphone (AmbeX)
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Use your device's built-in microphone (laptop, tablet, phone)
                    </Typography>
                    <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
                      ✓ Works on any device • ✓ No setup required • ✓ Mobile friendly
                    </Typography>
                  </Box>
                }
                sx={{ width: '100%', m: 0 }}
              />
            </Paper>

            {/* Hardware Cabin Microphone Option */}
            <Paper
              elevation={selectedSource === 'hardware' ? 3 : 1}
              sx={{
                p: 2,
                border: selectedSource === 'hardware' ? 2 : 1,
                borderColor: selectedSource === 'hardware' ? 'primary.main' : 'divider',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  elevation: 3,
                  borderColor: 'primary.main'
                }
              }}
              onClick={() => setSelectedSource('hardware')}
            >
              <FormControlLabel
                value="hardware"
                control={<Radio />}
                label={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Cabin fontSize="small" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        Hardware Cabin Microphone
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      Use a dedicated USB microphone in a consultation room
                    </Typography>
                    <Typography variant="caption" color="secondary" sx={{ mt: 0.5, display: 'block' }}>
                      ✓ Professional audio quality • ✓ Dedicated rooms • ✓ No browser required
                    </Typography>
                  </Box>
                }
                sx={{ width: '100%', m: 0 }}
              />
            </Paper>
          </RadioGroup>
        </FormControl>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
        >
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Import React at the top
import React from 'react';
